import type { InvoiceTag } from "@/apis/gql/types";
import type { InvoiceRowData } from "@/components/shared/data-table";
import { AutoBuchungTag } from "@/components/shared/tags/auto-buchung";
import { Badge } from "@/components/ui/badge";
import { InvoiceStatus } from "@/lib/constants/invoiceStatus";
import { CompanyRole } from "@/lib/miscellaneous/authorization";
import { Tags, tagNameToColor } from "@/lib/miscellaneous/tags";
import { useTenant } from "@/providers/session";
import type { CellContext } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const TagsCell = ({
  row,
  clientView = false,
}: CellContext<InvoiceRowData, unknown> & { clientView?: boolean }) => {
  const { t } = useTranslation();
  const { role } = useTenant();

  const rowTags: InvoiceTag[] = useMemo(() => {
    if (role === CompanyRole.CLIENT) {
      return (
        row.original?.tags?.filter(
          (tag) => tag.tagName === Tags.AdressatPruefen,
        ) ?? []
      ).map((tag) => ({
        ...tag,
        details: tag.details || null,
      }));
    }
    return (row.original.tags ?? []).map((tag) => ({
      ...tag,
      details: tag.details || null,
    }));
  }, [row.original.tags, role]);

  if (
    row.original.invoice.status === InvoiceStatus.NEW ||
    row.original.invoice.status === InvoiceStatus.ERROR
  ) {
    return null;
  }

  const duplicate = row.original.invoice.isDuplicate;

  const additionalTags = [];

  if (duplicate) {
    additionalTags.push({
      id: "duplicate_invoice",
      tagName: t("duplicate_invoice"),
      color: tagNameToColor(Tags.DuplicateInvoice),
    });
  }

  if (!row.original.invoice.isBooking && role !== CompanyRole.CLIENT) {
    additionalTags.push({
      id: "not_a_booking",
      tagName: t("not_a_booking_alert.title"),
      color: tagNameToColor(Tags.KeineBuchung),
    });
  }

  return (
    <div
      className="flex flex-wrap overflow-hidden"
      style={{
        minHeight: "30px", // Ensure a minimum height so it is visible
      }}
    >
      {[...rowTags, ...additionalTags]
        ?.sort((a, b) => a.tagName.localeCompare(b.tagName))
        ?.map((tag, index) => {
          if (tag.tagName === Tags.AutoBuchung) {
            return <AutoBuchungTag />;
          }

          return (
            <Badge
              className="text-xs m-1"
              key={index.toString()}
              style={{
                backgroundColor: tagNameToColor(tag.tagName).backgroundColor,
                color: tagNameToColor(tag.tagName).textColor,
              }}
            >
              {tag.tagName}
            </Badge>
          );
        })}
    </div>
  );
};
